export const PROMOTION_OFFER_TYPES = [
  'PRE_ORDER',
  'DURING_ORDER',
  'BEFORE_GAME',
  'AFTER_ORDER',
] as const;

export type PromotionOfferType = (typeof PROMOTION_OFFER_TYPES)[number];

export type PromotionOffer = {
  id: string;
  type: PromotionOfferType;
  quantity: number;
  percentage: number;
  highlight: boolean;
  title: string | null;
  description: string | null;
  sortOrder: number;
};

export type AcceptedOffer = {
  offerId: string;
  ticketsAmount: number;
};

export type LegacyPromotionPack = {
  quantity: number;
  percentage: number;
  highlight?: boolean;
};

export type UpsellOption = {
  id: string;
  title: string;
  quantity: number;
  percentage: number;
  value: number;
  highlight: boolean;
  icon: string;
};

function round2(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

export function resolveOfferForTickets({
  offers,
  type,
  ticketsAmount,
}: {
  offers: PromotionOffer[];
  type: PromotionOfferType;
  ticketsAmount: number;
}): PromotionOffer | undefined {
  return offers
    .filter((offer) => offer.type === type && offer.quantity <= ticketsAmount)
    .sort((a, b) => b.quantity - a.quantity)[0];
}

export function offerPrice({
  quantity,
  unitPrice,
  percentage,
}: {
  quantity: number;
  unitPrice: number;
  percentage: number;
}): number {
  return round2(quantity * unitPrice * (1 - percentage / 100));
}

export function calculatePackTotal({
  offers,
  ticketsAmount,
  unitPrice,
}: {
  offers: PromotionOffer[];
  ticketsAmount: number;
  unitPrice: number;
}): number {
  const pack = resolveOfferForTickets({ offers, type: 'PRE_ORDER', ticketsAmount });

  return offerPrice({
    quantity: ticketsAmount,
    unitPrice,
    percentage: pack?.percentage ?? 0,
  });
}

function fromLegacyPacks(packs: LegacyPromotionPack[]): PromotionOffer[] {
  return packs
    .filter((pack) => pack.quantity > 0)
    .map((pack, index) => ({
      id: `legacy-${index}`,
      type: 'PRE_ORDER' as const,
      quantity: pack.quantity,
      percentage: pack.percentage,
      highlight: Boolean(pack.highlight),
      title: null,
      description: null,
      sortOrder: index,
    }));
}

export function normalizePackOffers({
  promotionOffers,
  legacyPacks,
}: {
  promotionOffers: PromotionOffer[] | undefined;
  legacyPacks: unknown;
}): PromotionOffer[] {
  if (promotionOffers) {
    return promotionOffers
      .filter((offer) => offer.type === 'PRE_ORDER' && offer.quantity > 0)
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }

  if (!Array.isArray(legacyPacks)) return [];

  return fromLegacyPacks(legacyPacks as LegacyPromotionPack[]);
}

export function bumpOffers(
  promotionOffers: PromotionOffer[] | undefined,
): PromotionOffer[] {
  if (!promotionOffers) return [];

  return promotionOffers
    .filter((offer) => offer.type === 'DURING_ORDER' && offer.quantity > 0)
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

export function upsellOffers(
  promotionOffers: PromotionOffer[] | undefined,
): PromotionOffer[] {
  if (!promotionOffers) return [];

  return promotionOffers
    .filter((offer) => offer.type === 'AFTER_ORDER' && offer.quantity > 0)
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

export function beforeGameOffers(
  promotionOffers: PromotionOffer[] | undefined,
): PromotionOffer[] {
  if (!promotionOffers) return [];

  return promotionOffers
    .filter((offer) => offer.type === 'BEFORE_GAME' && offer.quantity > 0)
    .sort((a, b) => a.sortOrder - b.sortOrder);
}
